import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IBannerMeta, IBannerNotification, NOTIFICATION_EVENTS, NotificationService } from '../../../contract-management/services/notification.service';
import { IconComponent } from '../../../@core/components/ui/icon/icon.component';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { IUser } from 'src/app/@core/models/model';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { USER_ROLE } from 'src/app/@core/models/enums';

@Component({
    selector: 'app-banner',
    standalone: true,
    imports: [CommonModule, IconComponent, ButtonComponent],
    templateUrl: './banner.component.html',
    styleUrl: './banner.component.scss'
})
export class BannerComponent implements OnInit {
    activeBanners: IBannerNotification[] = [];
    currentIndex = 0;
    currentUser: IUser | null = null;

    constructor(private notificationService: NotificationService, private authService: AuthenticationService) {

        this.authService.currentUser.subscribe(res => {
            // Skip banner fetch for anonymous users (e.g. guests viewing shared documents)
            if (res && !res.anonymous) {
                this.currentUser = res;
                this.loadBanners();
            } else {
                this.currentUser = null;
                this.activeBanners = [];
                this.currentIndex = 0;
            }
        });
    }

    ngOnInit(): void {
    }

    private loadBanners(): void {
        this.notificationService.getBanners().subscribe(res => {
            if (res.success) {
                this.activeBanners = res.data || [];
                this.currentIndex = 0;
            }
        });
    }

    get currentBanner(): IBannerMeta | null {
        if (!this.activeBanners.length || this.currentIndex < 0 || this.currentIndex >= this.activeBanners.length) {
            return null;
        }
        const description = this.getTitle(this.activeBanners[this.currentIndex]);
        return { ...this.activeBanners[this.currentIndex].meta, description };
    }


    getTitle(notification: IBannerNotification): string {
        const isAdmin = this.currentUser?.role.includes(USER_ROLE.ADMIN) || this.currentUser?.role.includes(USER_ROLE.SUPER_ADMIN);
        switch (notification.event.type) {
            case NOTIFICATION_EVENTS.TRIAL.EXPIRED_GRACE: {
                const days = notification.event.data.endDate ? Math.ceil((new Date(notification.event.data.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 30;

                if (isAdmin) {
                    return `Your account is scheduled for deactivation in ${days} days, and your data will be permanently deleted`;
                }
                return `Your account is scheduled for deactivation in ${days} days, and your data will be permanently deleted. Contact your account administrator `;
            }
            case NOTIFICATION_EVENTS.TRIAL.INACTIVE_PENDING_DELETION: {
                const days = notification.event.data.endDate ? Math.ceil((new Date(notification.event.data.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 30;
                //  data: {
                //     startDate: now.toISOString(),
                //     endDate: deletionDate.toISOString(),
                //     phase: TRIAL_LIFECYCLE_PHASE.INACTIVE_PENDING_DELETION
                // }
                if (isAdmin) {
                    return `Your account is scheduled for deactivation in ${days} days, and your data will be permanently deleted. Upgrade your plan to retain access and keep your data safe.`;
                }
                return `Your account is scheduled for deactivation in ${days} days, and your data will be permanently deleted. Contact your account administrator `;
            }
            case NOTIFICATION_EVENTS.TRIAL.PAYMENT_FAILURE_GRACE: {
                if (isAdmin) {
                    return `We had trouble processing your payment. Please update your billing information to avoid service interruption.`;
                }
                return 'We had trouble processing your payment. Please update your billing information to avoid service interruption.';
            }
            case NOTIFICATION_EVENTS.SUBSCRIPTION.CANCELLED_GRACE: {
                const days = notification.event.data.endDate
                    ? Math.ceil((new Date(notification.event.data.endDate as string).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    : 30;
                if (isAdmin) {
                    return `Your subscription has been cancelled. You will lose access in ${days} days and your data will be permanently deleted unless you reactivate.`;
                }
                return `Your subscription has been cancelled. Access will end in ${days} days. Contact your account administrator to reactivate.`;
            }
            case NOTIFICATION_EVENTS.SUBSCRIPTION.INACTIVE_PENDING_DELETION: {
                const days = notification.event.data.endDate
                    ? Math.ceil((new Date(notification.event.data.endDate as string).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    : 30;
                if (isAdmin) {
                    return `Your account is now inactive. Your data will be permanently deleted in ${days} days unless you reactivate your subscription.`;
                }
                return `Your account is now inactive. Data will be permanently deleted in ${days} days. Contact your account administrator.`;
            }
            default:
                return notification.meta.title;
        }
    }

    prev(): void {
        if (this.currentIndex > 0) this.currentIndex--;
    }

    next(): void {
        if (this.currentIndex < this.activeBanners.length - 1) this.currentIndex++;
    }

    getBannerBgClass(severity: string): string {
        switch (severity) {
            case 'info': return 'bg-blue-50 border-blue-200';
            case 'warning': return 'bg-yellow-50 border-yellow-200';
            case 'error': return 'bg-red-50 border-red-200';
            default: return 'bg-blue-50 border-blue-200';
        }
    }

    getTextClass(severity: string): string {
        switch (severity) {
            case 'info': return 'text-blue-800';
            case 'warning': return 'text-yellow-800';
            case 'error': return 'text-red-800';
            default: return 'text-blue-800';
        }
    }

    getIconName(severity: string): string {
        switch (severity) {
            case 'info': return 'info';
            case 'warning': return 'triangle-alert';
            case 'error': return 'circle-alert';
            default: return 'info';
        }
    }

    getIconClass(severity: string): string {
        switch (severity) {
            case 'info': return 'text-blue-500';
            case 'warning': return 'text-yellow-500';
            case 'error': return 'text-red-500';
            default: return 'text-blue-500';
        }
    }

    getNavButtonClass(severity: string): string {
        switch (severity) {
            case 'info': return 'hover:bg-blue-100 disabled:opacity-40';
            case 'warning': return 'hover:bg-yellow-100 disabled:opacity-40';
            case 'error': return 'hover:bg-red-100 disabled:opacity-40';
            default: return 'hover:bg-blue-100 disabled:opacity-40';
        }
    }
}
